import { useMemo } from 'react';
import type { CareStateDiagnosisT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { formatIcd10CodeForDisplay } from '@/shared/lib/formatters';
import {
  DENSE_TABLE_CELL_CLASS,
  DENSE_TABLE_CONTAINER_CLASS,
  DENSE_TABLE_HEAD_CLASS,
  DENSE_TABLE_HEADER_CLASS,
  DENSE_TABLE_HEADER_ROW_CLASS,
  DENSE_TABLE_ROW_CLASS,
} from '@/shared/ui/primitives';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

export function DiagnosedConditionsTable() {
  const { careState, status } = useCareStateDomain();

  const diagnoses = useMemo<CareStateDiagnosisT[]>(() => {
    if (careState === null) return [];
    // UUID7 ids are time-ordered, so sorting by id descending surfaces the
    // most recently recorded diagnoses first.
    return [...careState.diagnoses].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  }, [careState]);

  if (status === 'loading') {
    return <div className="text-sm text-muted-foreground">Loading diagnoses…</div>;
  }

  if (status === 'error') {
    return (
      <div className="text-sm text-destructive">
        Could not load diagnoses. Please try refreshing.
      </div>
    );
  }

  if (diagnoses.length === 0) {
    return <div className="text-sm text-muted-foreground">No Lotus-recorded diagnoses yet.</div>;
  }

  return (
    <div className={DENSE_TABLE_CONTAINER_CLASS}>
      <Table>
        <TableHeader className={DENSE_TABLE_HEADER_CLASS}>
          <TableRow className={DENSE_TABLE_HEADER_ROW_CLASS}>
            <TableHead className={DENSE_TABLE_HEAD_CLASS}>ICD-10</TableHead>
            <TableHead className={DENSE_TABLE_HEAD_CLASS}>Condition</TableHead>
            <TableHead className={DENSE_TABLE_HEAD_CLASS}>Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {diagnoses.map((diagnosis) => (
            <TableRow key={diagnosis.id} className={DENSE_TABLE_ROW_CLASS}>
              <TableCell className={DENSE_TABLE_CELL_CLASS}>
                <span className="font-mono">{formatIcd10CodeForDisplay(diagnosis.icd10_code)}</span>
              </TableCell>
              <TableCell className={DENSE_TABLE_CELL_CLASS}>
                {diagnosis.icd10_description}
              </TableCell>
              <TableCell className={DENSE_TABLE_CELL_CLASS}>{diagnosis.note.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
